import { Box, Chip, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';

import { TranslateText } from '../../../../../../application/language/TranslateText';
import { RatingProgress } from '../../../../../analytics/shared/ratings/RatingProgress';
import { Colors } from '../../../../../design/color/Colors';
import { IAnalyticsUserUnitCard } from '../store/AnalyticsUserDTO';

type IUserUnitCardsContentProperties = {
  unitCards: IAnalyticsUserUnitCard[];
};

export const UserUnitCardsContent = observer(({ unitCards }: IUserUnitCardsContentProperties) => {
  const getStatusColor = (status: IAnalyticsUserUnitCard['status']) => {
    if (status === 'completed') return { backgroundColor: Colors.success, color: Colors.textPrimary };
    if (status === 'failed') return { backgroundColor: Colors.error, color: Colors.textPrimary };
    if (status === 'inProgress') return { backgroundColor: Colors.warning, color: Colors.textPrimary };
    return { borderColor: Colors.border, color: Colors.textSecondary };
  };

  const getStatusLabel = (status: IAnalyticsUserUnitCard['status']) => {
    if (status === 'completed') return <TranslateText i18nKey="analytics.userUnitStatuses.completed" />;
    if (status === 'failed') return <TranslateText i18nKey="analytics.userUnitStatuses.failed" />;
    if (status === 'inProgress') return <TranslateText i18nKey="analytics.userUnitStatuses.inProgress" />;
    return <TranslateText i18nKey="analytics.userUnitStatuses.notStarted" />;
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 2,
      }}
    >
      {unitCards.length ? (
        unitCards.map((unit) => (
          <Paper
            key={unit.id}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: Colors.bgElevated,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {unit.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${unit.earned} / ${unit.maxScore}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 80, gap: 1 }}>
              <RatingProgress value={unit.progress} />
              <Chip
                label={getStatusLabel(unit.status)}
                variant={unit.status === 'notStarted' ? 'outlined' : 'filled'}
                size="small"
                sx={{ ...getStatusColor(unit.status) }}
              />
            </Box>
          </Paper>
        ))
      ) : (
        <Typography color="text.secondary">
          <TranslateText i18nKey="analytics.noResults" />
        </Typography>
      )}
    </Box>
  );
});
